// Developer: Nithin | Date: 2026-07-26 | Added shift details section to employee view modal
import React from "react";
import { X, User, Mail, Phone, MapPin, Briefcase, Calendar, DollarSign, Building, FileText, Droplet, Heart, History, Building2, Clock } from "lucide-react";
import { EMPLOYMENT_STATUS_COLORS, EMPLOYMENT_STATUS_LABELS, EMPLOYMENT_TYPE_LABELS, getDepartmentLabel } from "../../constants/hrConstants";
import { API_BASE_URL } from "@/services/api";
import { useHR } from "../../context/HRContext";

function InfoRow({ icon: Icon, label, value, fallback = null }) {
  const displayVal = value != null && String(value).trim() !== "" ? value : fallback;
  if (!displayVal) return null;
  const isFallback = displayVal === "Not Provided" || displayVal === "—";
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
      </div>
      <div>
        <p className="text-[11px] text-slate-400 font-medium">{label}</p>
        <p className={`text-[13px] ${isFallback ? "text-slate-400 italic font-normal" : "text-slate-700 font-semibold"}`}>{displayVal}</p>
      </div>
    </div>
  );
}

export function EmployeeDetailModal({ employee, onClose }) {
  const { shifts } = useHR();
  if (!employee) return null;

  // Filter shifts belonging to this employee
  const empShifts = (shifts || []).filter(
    (s) => s.employeeId === (employee.employeeId || employee.id)
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl animate-in fade-in zoom-in-95 duration-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header banner */}
        <div className="bg-gradient-to-br from-[#2b448f] to-[#1e3070] px-6 py-6 text-white flex-shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/20 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-2xl font-bold">
              {employee.name?.charAt(0)}
            </div>
            <div>
              <h2 className="text-[17px] font-bold">{employee.name}</h2>
              <p className="text-[13px] text-blue-200">{employee.designation}</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium mt-1 ${EMPLOYMENT_STATUS_COLORS[employee.status] || "bg-white/20 text-white"}`}>
                {EMPLOYMENT_STATUS_LABELS[employee.status] || employee.status}
              </span>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4 text-[12px] text-blue-200">
            <span>ID: <span className="text-white font-mono font-semibold">{employee.id}</span></span>
            <span>•</span>
            <span className="capitalize">{EMPLOYMENT_TYPE_LABELS[employee.employmentType] || employee.employmentType}</span>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          
          {/* Section: Personal Information */}
          <div>
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Personal Information</h3>
            <div className="grid grid-cols-2 gap-y-5 gap-x-4">
              <InfoRow icon={Mail} label="Email" value={employee.email} />
              <InfoRow icon={Phone} label="Phone" value={employee.phone} />
              <InfoRow icon={User} label="Gender" value={employee.gender ? employee.gender.charAt(0).toUpperCase() + employee.gender.slice(1) : null} />
              <InfoRow icon={Calendar} label="Date of Birth" value={employee.dob ? new Date(employee.dob).toLocaleDateString("en-IN") : null} />
              <InfoRow icon={Droplet} label="Blood Group" value={employee.bloodGroup} />
              <InfoRow icon={Heart} label="Religion" value={employee.religion} />
            </div>
          </div>

          {/* Section: Job Information */}
          <div>
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Job Information</h3>
            <div className="grid grid-cols-2 gap-y-5 gap-x-4">
              <InfoRow icon={Building} label="Department" value={getDepartmentLabel(employee.department)} />
              <InfoRow icon={Briefcase} label="Role" value={employee.role} />
              <InfoRow icon={MapPin} label="Work Location" value={employee.workLocation} />
              <InfoRow icon={User} label="Reporting Manager" value={employee.reportingManager} />
              <InfoRow icon={Calendar} label="Joining Date" value={employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString("en-IN") : null} />
              <InfoRow icon={DollarSign} label="CTC (Yearly)" value={employee.salary ? `₹${Number(employee.salary).toLocaleString("en-IN")}/year` : null} />
            </div>
          </div>

          {/* Section: Bank Details */}
          {(() => {
            if (employee.source === 'user') return null;

            const bName = employee.bankName || employee.bankDetails?.bankName;
            const aHolder = employee.accountHolderName || employee.bankDetails?.accountHolderName;
            const aNum = employee.accountNumber || employee.bankDetails?.accountNumber;
            const ifsc = employee.ifscCode || employee.bankDetails?.ifscCode;
            const branch = employee.branchName || employee.bankDetails?.branchName;
            const aType = employee.accountType || employee.bankDetails?.accountType;

            return (
              <div>
                <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Bank Details</h3>
                <div className="grid grid-cols-2 gap-y-5 gap-x-4">
                  <InfoRow icon={Building2} label="Bank Name" value={bName} fallback="Not Provided" />
                  <InfoRow icon={User} label="Account Holder Name" value={aHolder} fallback="Not Provided" />
                  <InfoRow icon={FileText} label="Account Number" value={aNum ? `•••• •••• ${String(aNum).slice(-4)}` : null} fallback="Not Provided" />
                  <InfoRow icon={FileText} label="IFSC Code" value={ifsc} fallback="Not Provided" />
                  <InfoRow icon={MapPin} label="Branch Name" value={branch} fallback="Not Provided" />
                  <InfoRow icon={Briefcase} label="Account Type" value={aType ? (String(aType).charAt(0).toUpperCase() + String(aType).slice(1)) : null} fallback="Not Provided" />
                </div>
              </div>
            );
          })()}

          {/* Section: Past Experience */}
          {(employee.experienceYears || employee.previousCompany) && (
            <div>
              <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Past Experience</h3>
              <div className="grid grid-cols-2 gap-y-5 gap-x-4">
                <InfoRow icon={History} label="Experience (Years)" value={employee.experienceYears ? `${employee.experienceYears} Years` : null} />
                <InfoRow icon={Building2} label="Previous Company" value={employee.previousCompany} />
              </div>
            </div>
          )}

          {/* Section: Documents */}
          {employee.documents?.length > 0 && (
            <div>
              <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Documents</h3>
              <div className="flex flex-wrap gap-3">
                {employee.documents.map((doc, idx) => (
                  <a 
                    key={idx} 
                    href={doc.url?.startsWith('/') ? `${API_BASE_URL}${doc.url}` : (doc.url || "#")} 
                    download={doc.name}
                    className="text-[12px] font-medium bg-blue-50 text-blue-700 px-3 py-2 rounded-lg border border-blue-100 hover:bg-blue-100 hover:border-blue-300 hover:shadow-md truncate max-w-[200px] shadow-sm transition-all flex items-center gap-2 group"
                    title={doc.name}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FileText className="w-4 h-4 text-blue-500 group-hover:text-blue-700 transition-colors" />
                    {doc.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Section: Notes */}
          {employee.notes && (
            <div>
              <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Notes</h3>
              <p className="text-[13px] text-slate-600 bg-slate-50 rounded-xl p-4 border border-slate-100 leading-relaxed">{employee.notes}</p>
            </div>
          )}

          {/* Section: Shift Details */}
          {empShifts.length > 0 && (
            <div>
              <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
                Shift Details
              </h3>
              <div className="space-y-3">
                {empShifts.map((s, idx) => {
                  const shiftEmoji = s.shift === "morning" ? "🌅" : s.shift === "night" ? "🌙" : "🕐";
                  const shiftLabel = s.shift
                    ? s.shift.charAt(0).toUpperCase() + s.shift.slice(1)
                    : "—";
                  return (
                    <div
                      key={s._id || s.id || idx}
                      className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-xl p-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0 text-base">
                        {shiftEmoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-semibold text-slate-800">{shiftLabel} Shift</span>
                          <span className="text-[11px] text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {s.startTime} – {s.endTime}
                          </span>
                        </div>
                        {s.notes && (
                          <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{s.notes}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end flex-shrink-0">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}
